import { z } from 'zod';

const RawAmount = z
  .string()
  .regex(/^-?\d+$/)
  .transform(BigInt);

export const VaultOverviewSchema = z.object({
  nav: z.string(),
  tvlUsdc: RawAmount,
  totalSupply: RawAmount,
  historicalApy: z.string().nullable(),
  performanceFeePercent: z.string(),
  earlyExitFeePercent: z.string(),
  asOf: z.string(),
});

export const VaultPositionSchema = z.object({
  coinsRaw: RawAmount,
  avgEntryNav: z.string(),
  valueUsdcRaw: RawAmount,
  cumulativeDepositedUsdcRaw: RawAmount,
  cumulativeWithdrawnUsdcRaw: RawAmount,
  realizedPnlUsdcRaw: RawAmount,
  unrealizedPnlUsdcRaw: RawAmount,
  highWaterMark: z.string(),
  lockTierId: z.string().nullable(),
  lockedUntil: z.string().nullable(),
});

export const VaultLockTierSchema = z.object({
  id: z.string(),
  lockDays: z.number().int().nonnegative(),
  label: z.string(),
  earlyExitFeePct: z.string(),
  maxProfitPct: z.string(),
  sortOrder: z.number().int(),
});

export const VaultLockTiersSchema = z.array(VaultLockTierSchema);

export const NavHistoryItemSchema = z.object({
  nav: z.string(),
  tvlUsdc: RawAmount,
  ts: z.string(),
});

export const NavHistorySchema = z.array(NavHistoryItemSchema);

export const VaultTradeSchema = z.object({
  id: z.string(),
  conditionId: z.string(),
  marketQuestion: z.string(),
  outcomeIndex: z.number().int(),
  side: z.enum(['BUY', 'SELL']),
  sharesAmount: z.string(),
  avgPriceUsdc: z.string(),
  totalUsdcRaw: RawAmount,
  createdAt: z.string(),
});

export const VaultTradesSchema = z.object({
  data: z.array(VaultTradeSchema),
  count: z.number().int(),
});

export const VaultActivityItemSchema = z.object({
  intentId: z.string(),
  type: z.enum(['deposit', 'withdraw']),
  amountRaw: RawAmount,
  coinsRaw: RawAmount.nullable(),
  chainId: z.string(),
  status: z.string().transform(s => s.toLowerCase()),
  createdAt: z.string(),
});

export const VaultActivitySchema = z.object({
  data: z.array(VaultActivityItemSchema),
  count: z.number().int(),
});

export type VaultOverview = z.infer<typeof VaultOverviewSchema>;
export type VaultPosition = z.infer<typeof VaultPositionSchema>;
export type VaultLockTier = z.infer<typeof VaultLockTierSchema>;
export type NavHistoryItem = z.infer<typeof NavHistoryItemSchema>;
export type VaultTrade = z.infer<typeof VaultTradeSchema>;
export type VaultTrades = z.infer<typeof VaultTradesSchema>;
export type VaultActivityItem = z.infer<typeof VaultActivityItemSchema>;
export type VaultActivity = z.infer<typeof VaultActivitySchema>;
